import React, { useState } from 'react';
import { Info, Share2, Copy } from 'lucide-react';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from './ui/tooltip';
import { Checkbox } from './ui/checkbox';
import BreakupChart, { ChartTypeToggle } from './BreakupChart';
import EditablePill from './EditablePill';
import SliderRow from './SliderRow';
import { ranges } from '../mock';
import { formatMoney, formatMoneyShort, parseMoneyInput, currencies } from '../lib/currency';
import { useAppSettings } from '../lib/i18n';
import { shareCalculation } from '../lib/share';

/** Compact radio: circular radio + title, with optional narration below. */
function RadioOption({ active, onClick, title, desc, accent = 'green' }) {
  const palette = {
    green: { dot: 'border-[#059669] bg-[#059669]', text: 'text-[#065F46] dark:text-emerald-300', box: 'border-[#059669] bg-[#ECFDF5] dark:bg-[#0B2A1D]' },
    violet: { dot: 'border-[#6366F1] bg-[#6366F1]', text: 'text-[#4338CA] dark:text-indigo-300', box: 'border-[#6366F1] bg-[#EEF2FF] dark:bg-[#1B1240]' },
  }[accent];
  return (
    <button
      type="button"
      role="radio"
      aria-checked={active}
      onClick={onClick}
      className={`flex items-center gap-2 w-full text-left rounded-md border px-2.5 py-1 transition-all ${
        active
          ? palette.box
          : 'border-slate-200 dark:border-[#1E2A44] bg-white dark:bg-[#0F1B36] hover:border-[#0176D3]'
      }`}
    >
      <span
        className={`relative h-4 w-4 shrink-0 rounded-full border-2 transition-all ${
          active ? palette.dot : 'border-slate-300 dark:border-slate-500 bg-white dark:bg-transparent'
        }`}
      >
        {active && (
          <span className="absolute inset-[3px] rounded-full bg-white" />
        )}
      </span>
      <span className="flex-1 min-w-0 leading-tight">
        <span className={`block text-xs font-bold ${active ? palette.text : 'text-slate-700 dark:text-slate-200'}`}>{title}</span>
        {desc && <span className="block text-[10px] text-slate-500 dark:text-slate-400 leading-snug">{desc}</span>}
      </span>
    </button>
  );
}

export default function EmiCalculator({
  loanAmount, setLoanAmount,
  interestRate, setInterestRate,
  tenure, setTenure,
  tenureType, setTenureType,
  moratoriumEnabled, setMoratoriumEnabled,
  moratoriumMonths, setMoratoriumMonths,
  moratoriumType, setMoratoriumType,
  interestMethod, setInterestMethod,
  chartType, setChartType,
  simulation,
  buildShareState,
}) {
  const { t, currency } = useAppSettings();
  const c = currencies[currency];
  const { summary, schedule } = simulation;
  const [shareMsg, setShareMsg] = useState('');

  const handleTenureType = (type) => {
    if (type === tenureType) return;
    if (type === 'Mo') setTenure(Math.min(360, tenure * 12));
    else setTenure(Math.max(1, Math.round(tenure / 12)));
    setTenureType(type);
  };

  const onShare = async () => {
    const res = await shareCalculation(buildShareState());
    if (res.ok) setShareMsg(res.method === 'native' ? 'Shared!' : 'Link copied!');
    else setShareMsg('Copy failed');
    setTimeout(() => setShareMsg(''), 2500);
  };

  const tenureMax = tenureType === 'Yr' ? ranges.loanTenureYr.max : ranges.loanTenureMo.max;
  const tenureMin = tenureType === 'Yr' ? ranges.loanTenureYr.min : ranges.loanTenureMo.min;

  return (
    <section className="emi-wrap pt-6">
      <div className="mb-5 flex items-start justify-between gap-3 flex-wrap">
        <div className="flex-1 min-w-0">
          <h1 className="text-2xl sm:text-3xl font-bold text-[#032D60] dark:text-white tracking-tight">{t('title')}</h1>
          <p className="mt-2 text-sm sm:text-base text-slate-600 dark:text-slate-400 leading-relaxed break-words">
            {t('subtitle')}
          </p>
        </div>
        <button
          onClick={onShare}
          className="btn-3d md:hidden inline-flex items-center gap-1.5 px-3 h-9 rounded-md bg-gradient-to-b from-[#0EA5FF] to-[#0176D3] text-white text-sm font-semibold shrink-0"
          data-testid="share-btn-mobile"
        >
          <Share2 className="h-4 w-4" /> Share
        </button>
      </div>
      {shareMsg && (
        <div className="mb-3 rounded-md bg-[#ECFDF5] dark:bg-[#0B2A1D] border border-[#A7F3D0] dark:border-[#0F5D42] px-3 py-2 text-sm text-[#065F46] dark:text-emerald-300 inline-flex items-center gap-2">
          <Copy className="h-3.5 w-3.5" /> {shareMsg}
        </div>
      )}

      <div className="rounded-2xl border border-slate-200 dark:border-[#1E2A44] bg-white dark:bg-[#101B33] shadow-[0_2px_16px_rgba(3,45,96,0.08)] overflow-hidden">
        <div className="grid grid-cols-1 lg:grid-cols-5">
          {/* Inputs */}
          <div className="lg:col-span-3 p-5 sm:p-7 space-y-6 border-b lg:border-b-0 lg:border-r border-slate-100 dark:border-[#1E2A44]">
            <SliderRow
              label={`${t('loan_amount')} (${c.symbol})`}
              value={loanAmount}
              onChange={setLoanAmount}
              min={ranges.loanAmount.min}
              max={ranges.loanAmount.max}
              step={ranges.loanAmount.step}
              pillProps={{
                format: (v) => formatMoney(v, currency),
                parse: parseMoneyInput,
                maxLength: 12,
                width: 'w-40 sm:w-48',
                tone: 'blue',
              }}
              helperLeft={formatMoneyShort(ranges.loanAmount.min, currency)}
              helperRight={formatMoneyShort(ranges.loanAmount.max, currency)}
            />
            <SliderRow
              label={t('rate_of_interest')}
              value={interestRate}
              onChange={(v) => setInterestRate(Number(Number(v).toFixed(2)))}
              min={ranges.interestRate.min}
              max={ranges.interestRate.max}
              step={ranges.interestRate.step}
              pillProps={{
                format: (v) => Number(v).toFixed(2),
                parse: (s) => Number(String(s).replace(/[^\d.]/g, '')),
                suffix: '%',
                width: 'w-24',
                tone: 'blue',
              }}
              helperLeft={`${ranges.interestRate.min}%`}
              helperRight={`${ranges.interestRate.max}%`}
            />
            <SliderRow
              label={t('loan_tenure')}
              value={tenure}
              onChange={setTenure}
              min={tenureMin}
              max={tenureMax}
              step={1}
              pillProps={{
                format: (v) => String(v),
                parse: (s) => Math.round(Number(String(s).replace(/[^\d.]/g, ''))),
                width: 'w-20',
                tone: 'blue',
              }}
              suffixOptions={['Yr', 'Mo']}
              suffixLabels={[t('yr'), t('mo')]}
              activeSuffix={tenureType}
              onSuffixChange={handleTenureType}
              helperLeft={`${tenureMin} ${tenureType === 'Yr' ? t('yr') : t('mo')}`}
              helperRight={`${tenureMax} ${tenureType === 'Yr' ? t('yr') : t('mo')}`}
            />

            {/* Interest Type — compact 2-col radio + info tooltip */}
            <div className="rounded-lg border border-[#A7F3D0] dark:border-[#0F5D42] bg-gradient-to-br from-[#F0FDF4] to-[#ECFDF5] dark:from-[#0B2A1D] dark:to-[#10362A] px-3 py-2 relative">
              <div className="flex items-center gap-2">
                <span className="text-[10px] font-bold text-[#065F46] dark:text-emerald-300 uppercase tracking-wide shrink-0">{t('interest_type')}</span>
                <TooltipProvider delayDuration={120}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        type="button"
                        className="ml-auto h-4 w-4 rounded-full flex items-center justify-center text-[#059669] dark:text-emerald-300 hover:bg-[#D1FAE5] dark:hover:bg-[#0F5D42] transition-colors"
                        aria-label="Interest type details"
                        data-testid="interest-type-info"
                      >
                        <Info className="h-3.5 w-3.5" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="top" className="max-w-xs bg-[#032D60] text-white border-0 p-3">
                      <div className="space-y-1.5 text-left">
                        <p><span className="font-bold">{t('reducing_balance')}:</span> {t('reducing_balance_desc')}</p>
                        <p><span className="font-bold">{t('flat_rate')}:</span> {t('flat_rate_desc')}</p>
                      </div>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              <div className="mt-1.5 grid grid-cols-2 gap-2" role="radiogroup" aria-label={t('interest_type')}>
                <RadioOption
                  active={interestMethod === 'reducing'}
                  onClick={() => setInterestMethod('reducing')}
                  title={t('reducing_balance')}
                  accent="green"
                />
                <RadioOption
                  active={interestMethod === 'flat'}
                  onClick={() => setInterestMethod('flat')}
                  title={t('flat_rate')}
                  accent="green"
                />
              </div>
            </div>

            {/* Summary cards */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <SummaryCard label={t('monthly_emi')} value={formatMoney(summary.emi, currency)} accent testid="summary-emi" />
              <SummaryCard label={t('principal')} value={formatMoneyShort(loanAmount, currency)} tone="teal" testid="summary-principal" />
              <SummaryCard label={t('total_interest')} value={formatMoneyShort(summary.totalInterest, currency)} tone="coral" testid="summary-total-interest" />
            </div>

            {/* Moratorium — compact */}
            <div className="rounded-lg border border-[#C7D2FE] dark:border-[#4C3FA8] bg-gradient-to-br from-[#EEF2FF] to-[#E0E7FF] dark:from-[#1B1240] dark:to-[#251A5A] px-3 py-2.5">
              <label className="flex items-center gap-2 cursor-pointer">
                <Checkbox
                  checked={moratoriumEnabled}
                  onCheckedChange={(v) => setMoratoriumEnabled(Boolean(v))}
                  className="h-4 w-4 border-2 border-[#6366F1] data-[state=checked]:bg-[#6366F1] data-[state=checked]:border-[#6366F1]"
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <span className="text-[10px] font-bold text-[#4338CA] dark:text-indigo-300 uppercase tracking-wide">{t('moratorium_enable')}</span>
                    <Info className="h-3 w-3 text-[#6366F1]" />
                  </div>
                  <p className="text-[10px] text-[#4338CA]/80 dark:text-indigo-300/80 leading-snug">{t('moratorium_desc')}</p>
                </div>
                {moratoriumEnabled && (
                  <EditablePill
                    value={moratoriumMonths}
                    onChange={setMoratoriumMonths}
                    min={ranges.moratoriumMonths.min}
                    max={ranges.moratoriumMonths.max}
                    format={(v) => String(v)}
                    parse={(s) => Math.round(Number(String(s).replace(/[^\d.]/g, '')))}
                    suffix={t('months_unit')}
                    width="w-16"
                    tone="violet"
                    size="sm"
                  />
                )}
              </label>

              {moratoriumEnabled && (
                <div className="mt-2 grid grid-cols-2 gap-2" role="radiogroup" aria-label={t('moratorium_enable')}>
                  <RadioOption
                    active={moratoriumType === 'full'}
                    onClick={() => setMoratoriumType('full')}
                    title={t('full_moratorium')}
                    desc={t('full_moratorium_desc')}
                    accent="violet"
                  />
                  <RadioOption
                    active={moratoriumType === 'interest'}
                    onClick={() => setMoratoriumType('interest')}
                    title={t('interest_only')}
                    desc={t('interest_only_desc')}
                    accent="violet"
                  />
                </div>
              )}
            </div>
          </div>

          {/* Chart panel */}
          <div className="lg:col-span-2 p-5 sm:p-7 bg-gradient-to-br from-[#F5FBFF] to-[#FDF2F8]/40 dark:from-[#0F1B36] dark:to-[#2A1024]/50 flex flex-col items-center justify-start">
            <div className="w-full flex items-center justify-between mb-3">
              <span className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wide">Chart</span>
              <ChartTypeToggle value={chartType} onChange={setChartType} />
            </div>
            <div className="w-full flex justify-center">
              <BreakupChart
                type={chartType}
                principal={loanAmount}
                interest={summary.totalInterest}
                moratoriumInterest={moratoriumEnabled && moratoriumType === 'full' ? summary.moratoriumInterestCapitalised : 0}
                schedule={schedule}
                size={310}
                chartId="primary-chart"
              />
            </div>

            <div className="mt-6 w-full space-y-3">
              <LegendRow color="#0176D3" label={t('principal')} value={formatMoney(loanAmount, currency)} />
              <LegendRow color="#F97066" label={t('total_interest')} value={formatMoney(summary.totalInterest, currency)} />
              {moratoriumEnabled && moratoriumType === 'full' && summary.moratoriumInterestCapitalised > 0 && (
                <LegendRow
                  color="#F59E0B"
                  label={t('moratorium_interest_added')}
                  value={formatMoney(summary.moratoriumInterestCapitalised, currency)}
                />
              )}
              {summary.creditDaysDiscount > 0 && (
                <LegendRow color="#0EA5FF" label="Credit-days discount" value={`- ${formatMoney(summary.creditDaysDiscount, currency)}`} />
              )}
              {summary.subventionApplied > 0 && (
                <LegendRow color="#059669" label="Subvention applied" value={`- ${formatMoney(summary.subventionApplied, currency)}`} />
              )}
              <div className="h-px bg-slate-200 dark:bg-[#1E2A44] my-2" />
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-600 dark:text-slate-400">{t('total_amount')}</span>
                <span className="text-lg font-bold text-[#032D60] dark:text-white tabular-nums" data-testid="summary-total-payment">
                  {formatMoney(summary.totalPayment, currency)}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function SummaryCard({ label, value, accent, tone, testid }) {
  const toneClasses = {
    teal: 'bg-gradient-to-b from-white to-[#ECFDF5] border-[#A7F3D0] text-[#065F46] dark:from-[#0B2A1D] dark:to-[#10362A] dark:border-[#0F5D42] dark:text-emerald-300',
    coral: 'bg-gradient-to-b from-white to-[#FEF2F2] border-[#FECACA] text-[#B91C1C] dark:from-[#2A1414] dark:to-[#3A1B1B] dark:border-[#7A2626] dark:text-red-300',
  };
  if (accent) {
    return (
      <div className="btn-3d rounded-xl px-4 py-3 border bg-gradient-to-b from-[#0EA5FF] to-[#0176D3] border-[#0176D3] text-white" data-testid={testid}>
        <div className="text-[11px] uppercase tracking-wide text-white/85 font-semibold">{label}</div>
        <div className="mt-1 text-base sm:text-lg font-bold tabular-nums text-white">{value}</div>
      </div>
    );
  }
  return (
    <div className={`btn-3d rounded-xl px-4 py-3 border ${toneClasses[tone] || 'bg-white border-slate-200 text-[#032D60]'}`} data-testid={testid}>
      <div className="text-[11px] uppercase tracking-wide opacity-80 font-semibold">{label}</div>
      <div className="mt-1 text-base sm:text-lg font-bold tabular-nums">{value}</div>
    </div>
  );
}

function LegendRow({ color, label, value }) {
  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2">
        <span className="h-3 w-3 rounded-sm shadow-sm" style={{ backgroundColor: color }} />
        <span className="text-sm text-slate-600 dark:text-slate-400">{label}</span>
      </div>
      <span className="text-sm font-semibold text-slate-800 dark:text-slate-200 tabular-nums">{value}</span>
    </div>
  );
}
